import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    financeApi,
    PaginatedResponse,
    NoteListItem,
    CreateNotePayload,
} from '../../services/financeApi';

interface FinanceNotesState {
    list: NoteListItem[];
    pagination: PaginatedResponse<NoteListItem>['meta']['pagination'] | null;
    customers: PaginatedResponse<NoteListItem>['meta']['customers'];
    loadingList: boolean;
    creatingNote: boolean;
    error: string | null;
}

const initialState: FinanceNotesState = {
    list: [],
    pagination: null,
    customers: [],
    loadingList: false,
    creatingNote: false,
    error: null,
};

export const fetchFinanceNotes = createAsyncThunk(
    'financeNotes/fetchList',
    async (params: { customerId?: number; page?: number; perPage?: number } = {}) => {
        return await financeApi.getNotes(params);
    }
);

export const createFinanceNote = createAsyncThunk(
    'financeNotes/create',
    async (payload: CreateNotePayload) => {
        const response = await financeApi.createNote(payload);
        return response.data;
    }
);

const financeNotesSlice = createSlice({
    name: 'financeNotes',
    initialState,
    reducers: {
        clearFinanceNotesError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFinanceNotes.pending, (state) => {
                state.loadingList = true;
                state.error = null;
            })
            .addCase(fetchFinanceNotes.fulfilled, (state, action: PayloadAction<PaginatedResponse<NoteListItem>>) => {
                state.loadingList = false;
                state.list = action.payload.data;
                state.pagination = action.payload.meta.pagination;
                state.customers = action.payload.meta.customers;
            })
            .addCase(fetchFinanceNotes.rejected, (state, action) => {
                state.loadingList = false;
                state.error = action.error.message || 'Failed to load debit/credit notes';
            });

        builder
            .addCase(createFinanceNote.pending, (state) => {
                state.creatingNote = true;
                state.error = null;
            })
            .addCase(createFinanceNote.fulfilled, (state, action: PayloadAction<NoteListItem>) => {
                state.creatingNote = false;
                state.list.unshift(action.payload);
                if (state.pagination) {
                    state.pagination.total += 1;
                }
            })
            .addCase(createFinanceNote.rejected, (state, action) => {
                state.creatingNote = false;
                state.error = action.error.message || 'Failed to create note';
            });
    },
});

export const { clearFinanceNotesError } = financeNotesSlice.actions;
export default financeNotesSlice.reducer;

